import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

const FIELD_CHARACTER_SETS = Object.freeze([
  {
    key: 'alpha',
    label: 'Letters only',
    description: 'Only letters A-Z are allowed.',
    pattern: /^[A-Za-z]+$/,
  },
  {
    key: 'numeric',
    label: 'Numbers only',
    description: 'Only digits 0-9 are allowed.',
    pattern: /^[0-9]+$/,
  },
  {
    key: 'alphanumeric',
    label: 'Letters and numbers',
    description: 'Only letters A-Z and digits 0-9 are allowed.',
    pattern: /^[A-Za-z0-9]+$/,
  },
]);

const LENGTH_RULE = Object.freeze({
  NONE: 'none',
  EXACT: 'exact',
  RANGE: 'range',
});

export const EMPTY_FORM_PROPERTIES_STATE = Object.freeze({
  isRequired: false,
  characterSet: '',
  lengthRule: LENGTH_RULE.NONE,
  exactLength: '',
  minLength: '',
  maxLength: '',
});

const numberToInput = (value) =>
  typeof value === 'number' ? String(value) : '';

const resolveLengthRule = (formProperties) => {
  if (formProperties.exactLength !== undefined) return LENGTH_RULE.EXACT;
  if (
    formProperties.minLength !== undefined ||
    formProperties.maxLength !== undefined
  ) {
    return LENGTH_RULE.RANGE;
  }
  return LENGTH_RULE.NONE;
};

export const toFormPropertiesState = (formProperties) => {
  if (!formProperties) return { ...EMPTY_FORM_PROPERTIES_STATE };

  return {
    isRequired: Boolean(formProperties.isRequired),
    characterSet: formProperties.characterSet ?? '',
    lengthRule: resolveLengthRule(formProperties),
    exactLength: numberToInput(formProperties.exactLength),
    minLength: numberToInput(formProperties.minLength),
    maxLength: numberToInput(formProperties.maxLength),
  };
};

const isBlank = (value) => String(value ?? '').trim() === '';

const parseLength = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed >= 1 ? parsed : null;
};

/**
 * Mirrors the backend `validateFormProperties` rules so invalid lengths are
 * caught before the payment type is submitted.
 */
export const validateFormPropertiesState = (state) => {
  const errors = {};

  if (state.lengthRule === LENGTH_RULE.EXACT) {
    if (isBlank(state.exactLength)) {
      errors.exactLength = 'Enter an exact length';
    } else if (parseLength(state.exactLength) === null) {
      errors.exactLength = 'Must be a whole number of at least 1';
    }
  }

  if (state.lengthRule === LENGTH_RULE.RANGE) {
    const minProvided = !isBlank(state.minLength);
    const maxProvided = !isBlank(state.maxLength);

    if (!minProvided && !maxProvided) {
      errors.minLength = 'Enter a minimum or a maximum length';
    }

    const min = minProvided ? parseLength(state.minLength) : undefined;
    const max = maxProvided ? parseLength(state.maxLength) : undefined;

    if (minProvided && min === null) {
      errors.minLength = 'Must be a whole number of at least 1';
    }
    if (maxProvided && max === null) {
      errors.maxLength = 'Must be a whole number of at least 1';
    }
    if (min && max && min > max) {
      errors.maxLength = 'Maximum length cannot be lower than the minimum';
    }
  }

  return errors;
};

export const evaluateValueAgainstState = (value, state) => {
  const normalized = String(value ?? '');
  if (normalized.trim() === '') return [];

  const violations = [];

  if (state.lengthRule === LENGTH_RULE.EXACT) {
    const exactLength = parseLength(state.exactLength);
    if (exactLength !== null && normalized.length !== exactLength) {
      violations.push({ key: 'exactLength', count: exactLength });
    }
  }

  if (state.lengthRule === LENGTH_RULE.RANGE) {
    const maxLength = parseLength(state.maxLength);
    const minLength = parseLength(state.minLength);
    if (maxLength !== null && normalized.length > maxLength) {
      violations.push({ key: 'maxLength', count: maxLength });
    }
    if (minLength !== null && normalized.length < minLength) {
      violations.push({ key: 'minLength', count: minLength });
    }
  }

  const characterSet = FIELD_CHARACTER_SETS.find(
    (set) => set.key === state.characterSet,
  );
  if (characterSet && !characterSet.pattern.test(normalized)) {
    violations.push({ key: `characterSet.${characterSet.key}` });
  }

  return violations;
};

const assignLength = (target, key, rawValue) => {
  const parsed = parseLength(rawValue);
  if (parsed !== null) target[key] = parsed;
};

export const toFormProperties = (state) => {
  const next = {};

  if (state.isRequired) next.isRequired = true;
  if (state.characterSet) next.characterSet = state.characterSet;

  if (state.lengthRule === LENGTH_RULE.EXACT) {
    assignLength(next, 'exactLength', state.exactLength);
  }

  if (state.lengthRule === LENGTH_RULE.RANGE) {
    assignLength(next, 'minLength', state.minLength);
    assignLength(next, 'maxLength', state.maxLength);
  }

  return Object.keys(next).length ? next : undefined;
};

const lengthInputProps = {
  inputMode: 'numeric',
  pattern: '[0-9]*',
  min: 1,
};

const FieldValidationSettings = ({ value, errors = {}, onChange }) => {
  const handleChange = (key) => (event) => {
    onChange({ ...value, [key]: event.target.value });
  };

  const selectedCharacterSet = FIELD_CHARACTER_SETS.find(
    (set) => set.key === value.characterSet,
  );
  const isUnknownCharacterSet = Boolean(
    value.characterSet && !selectedCharacterSet,
  );

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={value.isRequired}
            onChange={(e) =>
              onChange({ ...value, isRequired: e.target.checked })
            }
            inputProps={{ 'data-testid': 'fieldSettingsRequiredSwitch' }}
          />
        }
        label='Required'
      />

      <FormControl size='small' sx={{ maxWidth: 260 }}>
        <InputLabel id='field-character-set-label'>
          Allowed characters
        </InputLabel>
        <Select
          labelId='field-character-set-label'
          label='Allowed characters'
          value={value.characterSet}
          onChange={handleChange('characterSet')}
          data-testid='fieldSettingsCharacterSetSelect'>
          <MenuItem value=''>Any characters</MenuItem>
          {isUnknownCharacterSet && (
            <MenuItem value={value.characterSet}>{value.characterSet}</MenuItem>
          )}
          {FIELD_CHARACTER_SETS.map((set) => (
            <MenuItem key={set.key} value={set.key}>
              {set.label}
            </MenuItem>
          ))}
        </Select>
        {isUnknownCharacterSet ? (
          <FormHelperText>
            Unrecognized character set — kept as configured.
          </FormHelperText>
        ) : (
          selectedCharacterSet && (
            <FormHelperText>{selectedCharacterSet.description}</FormHelperText>
          )
        )}
      </FormControl>

      <FormControl>
        <FormLabel>Length</FormLabel>
        <RadioGroup
          row
          value={value.lengthRule}
          onChange={handleChange('lengthRule')}>
          <FormControlLabel
            value={LENGTH_RULE.NONE}
            control={<Radio />}
            label='No limit'
          />
          <FormControlLabel
            value={LENGTH_RULE.EXACT}
            control={<Radio />}
            label='Exact'
          />
          <FormControlLabel
            value={LENGTH_RULE.RANGE}
            control={<Radio />}
            label='Range'
          />
        </RadioGroup>
      </FormControl>

      {value.lengthRule === LENGTH_RULE.EXACT && (
        <TextField
          size='small'
          label='Exact length'
          value={value.exactLength}
          onChange={handleChange('exactLength')}
          error={Boolean(errors.exactLength)}
          helperText={errors.exactLength}
          sx={{ maxWidth: 200 }}
          inputProps={{
            ...lengthInputProps,
            'data-testid': 'fieldSettingsExactLengthInput',
          }}
        />
      )}

      {value.lengthRule === LENGTH_RULE.RANGE && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size='small'
            label='Minimum length'
            value={value.minLength}
            onChange={handleChange('minLength')}
            error={Boolean(errors.minLength)}
            helperText={errors.minLength}
            sx={{ flex: 1, maxWidth: 200 }}
            inputProps={{
              ...lengthInputProps,
              'data-testid': 'fieldSettingsMinLengthInput',
            }}
          />
          <TextField
            size='small'
            label='Maximum length'
            value={value.maxLength}
            onChange={handleChange('maxLength')}
            error={Boolean(errors.maxLength)}
            helperText={errors.maxLength}
            sx={{ flex: 1, maxWidth: 200 }}
            inputProps={{
              ...lengthInputProps,
              'data-testid': 'fieldSettingsMaxLengthInput',
            }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default FieldValidationSettings;
